import { z } from 'zod';

// Webpack's DefinePlugin injects missing/empty env keys as "" (empty string),
// so this wrapper normalizes "" → undefined before validation.
//
// Place `.default(d)` on the schema passed to `optional()`, e.g.
// `optional(z.string().default(''))`, not on the result. `.default()` only
// fires when its input is `undefined`; since the preprocess converts "" to
// undefined, the default must sit inside the preprocess to catch it.
const optional = (schema) =>
  z.preprocess((v) => (v === '' ? undefined : v), schema);

// Boolean from a "true"/"false" string env var. `def` is the fallback used when
// the var is empty or absent.
const boolString = (def) =>
  optional(
    z
      .enum(['true', 'false'])
      .transform((v) => v === 'true')
      .default(def),
  );

/**
 * Centralized, Zod-validated env config. Imported once at boot; every
 * consumer reads from this module instead of touching process.env directly.
 *
 * Where values come from:
 *   Local  -> .env then .env.local (webpack dotenv; .env.local wins)
 *   CI/CD  -> GitHub Environment vars, injected by stg.yml / main.yml
 *
 * React Query tuning — expected values per environment:
 *   development : STALE=30000  GC=300000   RETRY=0  REFETCH_FOCUS=true
 *   staging     : STALE=60000  GC=600000   RETRY=2  REFETCH_FOCUS=true
 *   production  : STALE=300000 GC=600000   RETRY=3  REFETCH_FOCUS=true
 *
 * The baseline default for STALE is 30000 ms (used when QUERY_STALE_TIME is
 * not set). Devs who want always-fresh data should add QUERY_STALE_TIME=0
 * to their .env.local.
 */
export const schema = z.object({
  // Empty disables address suggestions. Photon is for local testing only.
  ADDRESS_AUTOCOMPLETE_PROVIDER: optional(z.string().optional()),
  // Key pública en el bundle; proteger con restricciones en Google Cloud.
  ADDRESS_AUTOCOMPLETE_KEY: optional(z.string().optional()),
  // Base URL for the recepthor-api backend (no trailing slash).
  // Default points at a local recepthor-api on :8080. This repo has no Docker Compose.
  BACKEND_BASE_URL: optional(z.string().url().default('http://localhost:8080')),

  // Cloudflare Turnstile public site key used in login / password-reset forms.
  // Obtain one at https://dash.cloudflare.com → Turnstile.
  // For local dev use the always-passing test key: 1x00000000000000000000AA
  TURNSTILE_SITE_KEY: z.string().min(1),

  // WhatsApp support number (country code, no "+"). Shown in the help modal.
  WHATSAPP_NUMBER: optional(z.string().min(1).default('56927443788')),

  // How long (ms) fetched data is considered fresh. While fresh, React Query
  // serves the cache without refetching. 0 in dev = always refetch.
  QUERY_STALE_TIME: optional(
    z.coerce.number().int().nonnegative().default(30000),
  ),

  // How long (ms) unused/inactive query data stays in memory before garbage
  // collection. 5 min default covers most tab-switching patterns.
  QUERY_GC_TIME: optional(
    z.coerce.number().int().nonnegative().default(300000),
  ),

  // Number of automatic retries on failed queries before surfacing the error
  // to the UI. 0 in dev = fail fast; 3 in prod = resilient to transient issues.
  QUERY_RETRY_COUNT: optional(z.coerce.number().int().nonnegative().default(0)),

  // Whether to refetch queries when the browser tab regains focus. Keeps data
  // fresh for users who switch between tabs.
  QUERY_REFETCH_ON_WINDOW_FOCUS: boolString(true),

  // --- Datadog RUM ---
  // Public application ID. Safe to commit/expose. Empty disables RUM.
  DD_RUM_APPLICATION_ID: optional(z.string().min(1).default('')),

  // Client token, bundled into the JS at build time. Low-sensitivity per
  // Datadog's RUM model, but injected via env (CI secret) rather than
  // hardcoded. Empty disables RUM.
  DD_RUM_CLIENT_TOKEN: optional(z.string().min(1).default('')),

  // Deployment environment tag for RUM events. CI sets this to 'staging' or
  // 'production'; defaults to 'development' for local builds.
  DD_ENV: optional(z.string().min(1).default('development')),

  // Build version tag. CI sets this to the commit SHA.
  DD_VERSION: optional(z.string().min(1).default('')),

  // Same-origin path prefix that proxies RUM/Logs intake to Datadog.
  // When set (e.g. "/dd-rum"), the SDK posts events to
  // `${origin}${DD_PROXY_PATH}/api/v2/{rum,logs}?...` and a CloudFront
  // behavior must forward those requests to browser-intake-datadoghq.com.
  // Empty = direct intake (subject to ad-blocker filtering).
  DD_PROXY_PATH: optional(
    z
      .string()
      .regex(/^\/[^?#]*$/, 'must start with /')
      .default(''),
  ),
});

const config = Object.freeze(
  schema.parse({
    ADDRESS_AUTOCOMPLETE_PROVIDER: process.env.ADDRESS_AUTOCOMPLETE_PROVIDER,
    ADDRESS_AUTOCOMPLETE_KEY: process.env.ADDRESS_AUTOCOMPLETE_KEY,
    BACKEND_BASE_URL: process.env.BACKEND_BASE_URL,
    TURNSTILE_SITE_KEY: process.env.TURNSTILE_SITE_KEY,
    WHATSAPP_NUMBER: process.env.WHATSAPP_NUMBER,
    QUERY_STALE_TIME: process.env.QUERY_STALE_TIME,
    QUERY_GC_TIME: process.env.QUERY_GC_TIME,
    QUERY_RETRY_COUNT: process.env.QUERY_RETRY_COUNT,
    QUERY_REFETCH_ON_WINDOW_FOCUS: process.env.QUERY_REFETCH_ON_WINDOW_FOCUS,
    DD_RUM_APPLICATION_ID: process.env.DD_RUM_APPLICATION_ID,
    DD_RUM_CLIENT_TOKEN: process.env.DD_RUM_CLIENT_TOKEN,
    DD_ENV: process.env.DD_ENV,
    DD_VERSION: process.env.DD_VERSION,
    DD_PROXY_PATH: process.env.DD_PROXY_PATH,
  }),
);

export const {
  ADDRESS_AUTOCOMPLETE_PROVIDER = '',
  ADDRESS_AUTOCOMPLETE_KEY = '',
  BACKEND_BASE_URL,
  TURNSTILE_SITE_KEY,
  WHATSAPP_NUMBER,
  QUERY_STALE_TIME,
  QUERY_GC_TIME,
  QUERY_RETRY_COUNT,
  QUERY_REFETCH_ON_WINDOW_FOCUS,
  DD_RUM_APPLICATION_ID,
  DD_RUM_CLIENT_TOKEN,
  DD_ENV,
  DD_VERSION,
  DD_PROXY_PATH,
} = config;
